import React, { useContext } from "react";

import { t } from "i18next";
import { useFormContext } from "react-hook-form";

import AppleIcon from "@/src/components/common/icons/AppleIcon";
import GoogleIcon from "@/src/components/common/icons/GoogleIcon";
import StoreIcon from "@/src/components/common/icons/StoreIcon";
import WebIcon from "@/src/components/common/icons/WebIcon";
import { Combobox } from "@/src/components/form-fields/Combobox";
import { PillField } from "@/src/components/form-fields/PillField";
import { SegmentedField } from "@/src/components/form-fields/SegmentedField";
import { SwitchField } from "@/src/components/form-fields/SwitchField";
import { TextField } from "@/src/components/form-fields/TextField";
import { mergePlanWithFormValues } from "@/src/components/offers/offerPlanAdapter";
import { getPlanResolutionOptions } from "@/src/components/offers/offerPlanView";
import {
  FREQUENCY_OPTION_LIST,
  getPlanFieldState,
  TRIAL_PRESET_LIST,
} from "@/src/components/offers/offersFormParams/offersFormParams";

import PlanAccessDurationField from "./fields/PlanAccessDurationField";
import PlanGeoField from "./fields/PlanGeoField";
import PlanMonetizationField from "./fields/PlanMonetizationField";
import PlanPaymentField from "./fields/PlanPaymentField";
import PlanPriceField from "./fields/PlanPriceField";
import PlanDrawerContainerContext, {
  PLAN_DRAWER_POPOVER_CLASS,
} from "./plan-drawer-context";

import type { JSX, ReactNode } from "react";

import type { ProviderPlan } from "@/src/api/OfferAPI.types";
import type { PlanFormValues } from "@/src/components/offers/offerPlanAdapter";
import type {
  AvailablePlatform,
  Retailer,
} from "@/src/components/offers/offersFormParams/offersFormParams";
import type { OfferPlanType } from "@/src/types";

interface OfferPlanDrawerSectionsProps {
  plan: OfferPlanType;
  availablePlatform: AvailablePlatform;
  availableProviderPlanList: ProviderPlan[];
  lockedProviderPlanList: ProviderPlan[];
  retailerList: Retailer[];
}

interface SectionProps {
  title: string;
  children: ReactNode;
}

function Section({ title, children }: SectionProps): JSX.Element {
  return (
    <section className="border-border border-b pb-8 last:border-b-0 last:pb-0">
      <div className="text-muted-foreground mb-2 text-base font-semibold tracking-wider uppercase">
        {title}
      </div>
      <div className="flex flex-col gap-5">{children}</div>
    </section>
  );
}

function OfferPlanDrawerSections({
  plan,
  availablePlatform,
  availableProviderPlanList,
  lockedProviderPlanList,
  retailerList,
}: OfferPlanDrawerSectionsProps): JSX.Element {
  const portalContainer = useContext(PlanDrawerContainerContext);
  const { watch } = useFormContext<PlanFormValues>();
  const derivedPlan = mergePlanWithFormValues(plan, watch());
  const fieldState = getPlanFieldState(derivedPlan);
  const isFree = derivedPlan.type === "free";
  const isSvod = derivedPlan.type === "svod";
  const isCommercialisationShown = [
    fieldState.enabled,
    fieldState.platform,
    fieldState.provider_plans,
    fieldState.platform_product_id,
    fieldState.storefront_retailer_uuid,
  ].some(({ hidden }) => !hidden);

  const platformOptionList = [
    {
      value: "okast",
      label: <WebIcon className="size-5" color="currentColor" />,
      disabled: !availablePlatform.okast,
    },
    {
      value: "apple",
      label: <AppleIcon className="size-5" color="currentColor" />,
      disabled: !availablePlatform.apple,
    },
    {
      value: "google",
      label: <GoogleIcon className="size-4.5" color="currentColor" />,
      disabled: !availablePlatform.google,
    },
    {
      value: "retailer",
      label: <StoreIcon className="size-5" color="currentColor" />,
      disabled: !availablePlatform.retailer,
    },
  ];

  const frequencyOptionList = FREQUENCY_OPTION_LIST.map((frequency) => ({
    value: String(frequency),
    label:
      frequency % 12 === 0
        ? t("editor-offers:year", { count: frequency / 12 })
        : t("editor-offers:month", { count: frequency }),
  }));

  const d2gDescription = fieldState.d2g_enabled.reasonKey
    ? t(`editor-offers:${fieldState.d2g_enabled.reasonKey}`)
    : t("editor-offers:downloadToGoDescription");

  return (
    <React.Fragment>
      {isCommercialisationShown ? (
        <Section
          title={
            isFree
              ? t("editor-offers:section_availability")
              : t("editor-offers:section_commercialisation")
          }
        >
          {!fieldState.enabled.hidden ? (
            <SwitchField
              description={t("editor-offers:statusDescription")}
              label={t("editor-offers:status")}
              name="enabled"
            />
          ) : null}
          {!fieldState.platform.hidden ? (
            <SegmentedField
              disabled={fieldState.platform.disabled}
              label={t("editor-offers:platform")}
              name="platform"
              options={platformOptionList}
            />
          ) : null}
          {!fieldState.provider_plans.hidden &&
          availableProviderPlanList.length > 0 ? (
            <PlanPaymentField
              activeProviderPlanList={lockedProviderPlanList}
              availableProviderPlanList={availableProviderPlanList}
            />
          ) : null}
          {!fieldState.platform_product_id.hidden ? (
            <TextField
              disabled={fieldState.platform_product_id.disabled}
              label={t("editor-offers:platform_product_id")}
              name="platform_product_id"
              placeholder={t("editor-offers:platform_product_id_placeholder")}
            />
          ) : null}
          {!fieldState.storefront_retailer_uuid.hidden ? (
            <Combobox
              disabled={fieldState.storefront_retailer_uuid.disabled}
              emptyLabel={t("editor-offers:comboboxEmpty")}
              label={t("editor-offers:operatorDcb")}
              name="storefront_retailer_uuid"
              placeholder={t("editor-offers:selectOperator")}
              popoverClassName={PLAN_DRAWER_POPOVER_CLASS}
              popoverContainer={portalContainer}
              searchPlaceholder={t("editor-offers:comboboxSearch")}
              options={retailerList.map((retailer) => ({
                value: retailer.value,
                label: retailer.label,
              }))}
            />
          ) : null}
        </Section>
      ) : null}
      {!fieldState.pricing.hidden ? (
        <Section title={t("editor-offers:section_pricing")}>
          <PlanPriceField disabled={fieldState.price.disabled} />
          {isSvod ? (
            <SegmentedField
              disabled={fieldState.frequency.disabled}
              label={t("editor-offers:billingFrequency")}
              name="frequency"
              options={frequencyOptionList}
              parse={Number}
            />
          ) : (
            <PlanAccessDurationField
              disabled={fieldState.accessDuration.disabled}
            />
          )}
        </Section>
      ) : null}
      {isFree ? (
        <Section title={t("editor-offers:section_temporality")}>
          <PlanAccessDurationField />
        </Section>
      ) : null}

      <Section title={t("editor-offers:section_settings")}>
        <SegmentedField
          label={t("editor-offers:resolution")}
          name="resolution"
          options={getPlanResolutionOptions()}
        />
        {isSvod && !fieldState.trial_duration.hidden ? (
          <PillField
            disabled={fieldState.trial_duration.disabled}
            label={t("editor-offers:trial_duration")}
            name="trial_duration"
            placeholder={t("editor-offers:otherDurationPlaceholder")}
            presets={TRIAL_PRESET_LIST}
            formatLabel={(days: number) =>
              days === 0
                ? t("editor-offers:none")
                : `${days} ${t("editor-analytics:frequency_type.day", { count: days })}`
            }
          />
        ) : null}
        <SwitchField
          description={d2gDescription}
          disabled={fieldState.d2g_enabled.disabled}
          label={t("editor-offers:downloadToGo")}
          name="d2g_enabled"
        />
        {!fieldState.donation_enabled.hidden ? (
          <SwitchField
            description={t("editor-offers:donationDescription")}
            disabled={fieldState.donation_enabled.disabled}
            label={t("editor-offers:donation_enabled")}
            name="donation_enabled"
          />
        ) : null}
        {!fieldState.gift_enabled.hidden ? (
          <SwitchField
            description={t("editor-offers:giftDescription")}
            disabled={fieldState.gift_enabled.disabled}
            label={t("editor-offers:gift_enabled")}
            name="gift_enabled"
          />
        ) : null}
        <PlanMonetizationField />
        {isFree ? (
          <SwitchField
            description={t("editor-offers:freemiumDescription")}
            label={t("editor-offers:freemium")}
            name="freemium"
          />
        ) : null}
      </Section>

      <Section title={t("editor-offers:section_restrictions")}>
        {!fieldState.require_registration.hidden ? (
          <SwitchField
            description={t("editor-offers:requireRegistrationDescription")}
            label={t("editor-offers:requireRegistrationLabel")}
            name="require_registration"
          />
        ) : null}
        <PlanGeoField disabled={fieldState.geoblock_mode.disabled} />
      </Section>
    </React.Fragment>
  );
}

export default OfferPlanDrawerSections;
